import {
  useForm,
  Modal,
  TextField,
  PrimaryButton,
  SecondaryButton,
  NumberField,
  DropdownField,
} from '@weave/design-system';
import { useCreateEntityTagMutation } from 'apis/wetags/mutations';
import { getTagStatsQuery, listEntityTagsQuery } from 'apis/wetags/queriesV2';
import { useEffect, useState } from 'react';
import { AsyncBatcher } from 'utils/AsyncBatcher';

const isLocalDev = window.location.hostname === 'localhost';

export function CreateEntityTagModal(props: {
  isOpen: boolean;
  onClose: () => void;
  namespaceId: string;
  tagId: string;
}) {
  const { isOpen, onClose, namespaceId, tagId } = props;
  const { mutateAsync: createEntityTag, isLoading } = useCreateEntityTagMutation();
  const [progress, setProgress] = useState<{
    completed: number;
    total: number;
    percentage: number;
  } | null>(null);

  // reset the form on modal close
  useEffect(() => {
    if (!isOpen) {
      reset();
      setProgress(null);
    }
  }, [isOpen]);

  const { formProps, getFieldProps, reset, values, isComplete, seedValues } = useForm({
    fields: {
      ids: {
        type: 'text',
      },
    },
    onSubmit: async (values) => {
      if (!values.ids) {
        return;
      }
      // Split the input by comma, newline, or space, then filter out empty strings
      const entityIds = processEntityIds(values.ids);

      // Create entity tag for each entity ID using batched operations
      const operations = entityIds.map(
        (entityId) => () =>
          createEntityTag({
            entityTag: {
              namespaceId,
              tagId,
              entityId,
            },
          })
      );

      const batcher = new AsyncBatcher(operations, 5, (progressUpdate) => {
        setProgress(progressUpdate);
      });

      await batcher.execute();

      // Invalidate stats query to refresh the count
      getTagStatsQuery.invalidate({ namespaceId, tagId });
      listEntityTagsQuery.invalidate({ namespaceId, tagId });

      // Close modal after successful creation
      onClose();
    },
  });

  const processEntityIds = (rawIds: string): string[] => {
    if (!rawIds) return [];
    return rawIds
      .split(/[,\n\s]+/)
      .map((id) => id.trim())
      .map((id) => id.replace(/['"]/g, '')) // replace quotes
      .filter((id) => id.length > 0);
  };

  const addRandomUUIDs = () => {
    const count = Math.floor(Math.random() * 9) + 2; // Random number between 2 and 10
    const uuids = Array.from({ length: count }, () => crypto.randomUUID());
    const currentValue = values.ids || '';
    const newValue = currentValue
      ? `${currentValue}\n${uuids.join('\n')}`
      : uuids.join('\n');

    seedValues({ ids: newValue });
  };

  const getEntityIdCount = () => {
    return processEntityIds(values.ids || '').length;
  };

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header>Add tag to entities</Modal.Header>
      <br />
      <Modal.Body>
        <p>
          This is where you can manually apply this tag to specific entities so that it's
          always on for them, regardless of the rollout percentage.
        </p>
        <form {...formProps}>
          <textarea
            style={{ width: '100%', height: '200px' }}
            placeholder="Enter entity IDs, separated by commas, spaces, or new lines"
            disabled={isLoading}
            {...(getFieldProps('ids') as any)}
          />
          <div style={{ marginTop: '0.1rem', fontSize: '0.75rem', color: '#666' }}>
            {getEntityIdCount()} entity ID{getEntityIdCount() === 1 ? '' : 's'}
            {progress && (
              <div style={{ marginTop: '0.5rem' }}>
                Processing: {progress.completed}/{progress.total} ({progress.percentage}%)
              </div>
            )}
          </div>

          <footer style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <PrimaryButton type="submit" disabled={!isComplete || isLoading}>
              Create
            </PrimaryButton>
            <SecondaryButton onClick={onClose} type="button" disabled={isLoading}>
              Cancel
            </SecondaryButton>
          </footer>
          <div style={{ marginTop: '1rem' }}>
            {isLocalDev && (
              <SecondaryButton type="button" onClick={addRandomUUIDs}>
                Add Random UUIDs
              </SecondaryButton>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
